import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormField, ProjectData } from '../types';
import { FileUpload } from './FileUpload';
import { HelpTooltip } from './HelpTooltip';
import { debounce } from 'lodash';

// Function to determine which section a field belongs to
const getFieldSection = (fieldName: string): string => {
  if (['appName', 'logoUrl', 'useLogoAsIcon', 'appIconUrl', 'primaryColor', 'secondaryColor', 'companyDescription'].includes(fieldName)) {
    return 'basic-app-info';
  }
  if (['useWebsiteForShop', 'redirectWebsiteUrl'].includes(fieldName)) {
    return 'app-store-contact';
  }
  if (['termsUrl', 'privacyUrl'].includes(fieldName)) {
    return 'legal-requirements';
  }
  if (['versiRentStoreId', 'versiRentUsername', 'versiRentPassword'].includes(fieldName)) {
    return 'versirent-integration';
  }
  if (['appStoreContactEmail', 'appStoreContactPhone', 'testerEmails', 'googlePlayBrandAssetAgreement'].includes(fieldName)) {
    return 'testing';
  }
  if (['appleDeveloperAccountName'].includes(fieldName)) {
    return 'apple-developer-setup';
  }
  if (['appleAccountInvitesSection', 'appleUserMwhorey', 'appleUserNmore', 'appleUserDev', 'appleUserCstack', 'appleUserDscarafiotti'].includes(fieldName)) {
    return 'apple-account-invites';
  }
  if (['teamKeyEnabled'].includes(fieldName)) {
    return 'team-key';
  }
  if (['targetAudience', 'features', 'integrations', 'customRequirements'].includes(fieldName)) {
    return 'app-details';
  }
  return 'other';
};

interface DynamicFormProps {
  fields: FormField[];
  initialData: ProjectData['formData'];
  onFormChange: (data: Partial<ProjectData['formData']>) => void;
  jiraKey: string;
  readonly?: boolean;
}

export function DynamicForm({ fields, initialData, onFormChange, jiraKey, readonly = false }: DynamicFormProps) {
  // Create validation schema dynamically
  const createValidationSchema = () => {
    const schemaFields: Record<string, any> = {};
    
    fields.forEach((field) => {
      // Skip section headers - they're not data fields (IS-1423)
      if (field.type === 'section') {
        return;
      }
      
      let fieldSchema: any;
      
      switch (field.type) {
        case 'text':
        case 'email':
        case 'textarea':
        case 'password':
          fieldSchema = z.string();
          if (field.validation?.minLength) {
            fieldSchema = fieldSchema.min(field.validation.minLength);
          }
          if (field.validation?.maxLength) {
            fieldSchema = fieldSchema.max(field.validation.maxLength);
          }
          if (field.validation?.pattern) {
            fieldSchema = fieldSchema.regex(new RegExp(field.validation.pattern));
          }
          if (field.type === 'email') {
            fieldSchema = fieldSchema.email();
          }
          break;
          
        case 'url':
          fieldSchema = z.string().url();
          if (field.validation?.pattern) {
            fieldSchema = fieldSchema.regex(new RegExp(field.validation.pattern));
          }
          break;
          
        case 'select':
          fieldSchema = z.string();
          break;
          
        case 'color':
          fieldSchema = z.string().regex(/^#[0-9A-F]{6}$/i);
          break;
          
        case 'multiselect':
          fieldSchema = z.array(z.string());
          break;
          
        case 'multiemail':
          fieldSchema = z.array(z.string().email());
          if (field.validation?.minEmails) {
            fieldSchema = fieldSchema.min(field.validation.minEmails);
          }
          if (field.validation?.maxEmails) {
            fieldSchema = fieldSchema.max(field.validation.maxEmails);
          }
          break;
          
        case 'checkbox':
          fieldSchema = z.boolean();
          break;
          
        case 'file':
          fieldSchema = z.string().optional();
          break;
          
        default:
          fieldSchema = z.string();
      }
      
      // Handle conditional validation for Custom Shop URL
      if (field.name === 'redirectWebsiteUrl') {
        fieldSchema = fieldSchema.refine((val: any, ctx: any) => {
          const useWebsiteForShop = ctx.parent?.useWebsiteForShop;
          if (useWebsiteForShop && (!val || val.toString().trim().length === 0)) {
            return false;
          }
          return true;
        }, 'Custom Shop URL is required when "Use my website for shop features" is checked');
      }
      
      // Handle conditional validation for App Icon URL
      if (field.name === 'appIconUrl') {
        fieldSchema = fieldSchema.refine((val: any, ctx: any) => {
          const useLogoAsIcon = ctx.parent?.useLogoAsIcon;
          // If using logo as icon, app icon is not required
          if (useLogoAsIcon) {
            return true;
          }
          // If not using logo as icon, app icon is required
          return val && val.toString().trim().length > 0;
        }, 'App Icon URL is required when not using logo as icon');
      }
      
      // Handle conditional validation for Notification Email
      if (field.name === 'notificationEmail') {
        fieldSchema = fieldSchema.refine((val: any, ctx: any) => {
          const enableEmailNotifications = ctx.parent?.enableEmailNotifications;
          // If email notifications disabled, email is not required
          if (!enableEmailNotifications) {
            return true;
          }
          // If email notifications enabled, email is required
          return val && val.toString().trim().length > 0;
        }, 'Notification email is required when email notifications are enabled');
      }
      
      // Handle conditional validation for Notification Phone
      if (field.name === 'notificationPhone') {
        fieldSchema = fieldSchema.refine((val: any, ctx: any) => {
          const enableSmsNotifications = ctx.parent?.enableSmsNotifications;
          // If SMS notifications disabled, phone is not required
          if (!enableSmsNotifications) {
            return true;
          }
          // If SMS notifications enabled, phone is required
          return val && val.toString().trim().length > 0;
        }, 'Notification phone is required when SMS notifications are enabled');
      }
      
      if (field.required) {
        fieldSchema = fieldSchema.refine((val: any) => {
          if (field.type === 'multiselect' || field.type === 'multiemail') {
            return Array.isArray(val) && val.length > 0;
          }
          if (field.type === 'file') {
            return val && val.length > 0;
          }
          return val && val.toString().trim().length > 0;
        }, `${field.label} is required`);
      } else {
        fieldSchema = fieldSchema.optional();
      }
      
      schemaFields[field.name] = fieldSchema;
    });
    
    return z.object(schemaFields);
  };

  const validationSchema = createValidationSchema();
  type FormData = z.infer<typeof validationSchema>;

  const {
    control,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: initialData as FormData,
  });

  // Debounced form change handler - increased delay to reduce Firestore updates
  const debouncedOnFormChange = React.useCallback(
    debounce((data: Partial<ProjectData['formData']>) => {
      onFormChange(data);
    }, 2000), // Increased from 500ms to 2 seconds
    [onFormChange]
  );

  // Watch form changes and trigger debounced update
  // Use a ref to track if we've already processed the initial values
  const hasProcessedInitial = React.useRef(false);
  const previousValues = React.useRef<Partial<ProjectData['formData']>>(initialData);
  const watchedValues = watch();
  
  React.useEffect(() => {
    // Skip the initial render to avoid unnecessary updates
    if (!hasProcessedInitial.current) {
      hasProcessedInitial.current = true;
      previousValues.current = watchedValues;
      return;
    }
    
    // Only update if the values have actually changed
    const hasChanged = JSON.stringify(watchedValues) !== JSON.stringify(previousValues.current);
    if (hasChanged) {
      previousValues.current = watchedValues;
      debouncedOnFormChange(watchedValues);
    }
  }, [watchedValues, debouncedOnFormChange]);

  const renderField = (field: FormField) => {
    const error = errors[field.name as keyof typeof errors];

    switch (field.type) {
      case 'text':
      case 'email':
      case 'password':
        return (
          <Controller
            name={field.name as keyof FormData}
            control={control}
            render={({ field: { onChange, value } }) => (
              <input
                type={field.type}
                className={`input ${error ? 'border-red-500' : ''} ${readonly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder={field.placeholder}
                value={value || ''}
                onChange={onChange}
                readOnly={readonly}
                disabled={readonly}
              />
            )}
          />
        );

      case 'url':
        return (
          <Controller
            name={field.name as keyof FormData}
            control={control}
            render={({ field: { onChange, value } }) => (
              <input
                type="url"
                className={`input ${error ? 'border-red-500' : ''} ${readonly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder={field.placeholder}
                value={value || ''}
                onChange={onChange}
                readOnly={readonly}
                disabled={readonly}
              />
            )}
          />
        );

      case 'textarea':
        return (
          <Controller
            name={field.name as keyof FormData}
            control={control}
            render={({ field: { onChange, value } }) => (
              <textarea
                className={`textarea ${error ? 'border-red-500' : ''} ${readonly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder={field.placeholder}
                value={value || ''}
                onChange={onChange}
                rows={4}
                readOnly={readonly}
                disabled={readonly}
              />
            )}
          />
        );

      case 'select':
        return (
          <Controller
            name={field.name as keyof FormData}
            control={control}
            render={({ field: { onChange, value } }) => (
              <select
                className={`select ${error ? 'border-red-500' : ''} ${readonly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                value={value || ''}
                onChange={onChange}
                disabled={readonly}
              >
                <option value="">Select an option</option>
                {field.options?.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            )}
          />
        );

      case 'color':
        return (
          <Controller
            name={field.name as keyof FormData}
            control={control}
            render={({ field: { onChange, value } }) => (
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  className={`w-12 h-10 rounded border border-gray-300 ${readonly ? 'cursor-not-allowed opacity-50' : ''}`}
                  value={value || '#000000'}
                  onChange={onChange}
                  disabled={readonly}
                />
                <input
                  type="text"
                  className={`input flex-1 ${error ? 'border-red-500' : ''} ${readonly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="#000000"
                  value={value || ''}
                  onChange={onChange}
                  readOnly={readonly}
                  disabled={readonly}
                />
              </div>
            )}
          />
        );

              case 'multiselect':
          return (
            <Controller
              name={field.name as keyof FormData}
              control={control}
              render={({ field: { onChange, value } }) => (
                <div className="space-y-2">
                  {field.options?.map((option) => (
                    <label key={option} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        className={`rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${readonly ? 'cursor-not-allowed opacity-50' : ''}`}
                        checked={Array.isArray(value) && value.includes(option)}
                        onChange={(e) => {
                          if (readonly) return;
                          const currentValue = Array.isArray(value) ? value : [];
                          if (e.target.checked) {
                            onChange([...currentValue, option]);
                          } else {
                            onChange(currentValue.filter((v) => v !== option));
                          }
                        }}
                        disabled={readonly}
                      />
                      <span className="text-sm text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
              )}
            />
          );

        case 'multiemail':
          return (
            <Controller
              name={field.name as keyof FormData}
              control={control}
              render={({ field: { onChange, value } }) => {
                // Store the raw input value separately from the processed emails array
                const [rawInput, setRawInput] = React.useState(
                  Array.isArray(value) ? value.join(', ') : ''
                );

                return (
                  <div className="space-y-2">
                    <textarea
                      className={`textarea ${error ? 'border-red-500' : ''} ${readonly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      placeholder={field.placeholder}
                      value={rawInput}
                      onChange={(e) => {
                        if (readonly) return;
                        const inputValue = e.target.value;
                        setRawInput(inputValue);
                        
                        // Process emails only when we have valid ones
                        const emails = inputValue
                          .split(',')
                          .map(email => email.trim())
                          .filter(email => email.length > 0 && email.includes('@'));
                        
                        onChange(emails);
                      }}
                      rows={3}
                      readOnly={readonly}
                      disabled={readonly}
                    />
                    <p className="text-xs text-gray-500">
                      Enter email addresses separated by commas
                    </p>
                  </div>
                );
              }}
            />
          );

        case 'checkbox':
          return (
            <Controller
              name={field.name as keyof FormData}
              control={control}
              render={({ field: { onChange, value } }) => (
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    className={`rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${readonly ? 'cursor-not-allowed opacity-50' : ''}`}
                    checked={value || false}
                    onChange={(e) => {
                      if (readonly) return;
                      onChange(e.target.checked);
                    }}
                    disabled={readonly}
                  />
                  <span className="text-sm text-gray-700">
                    {field.helperText}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </span>
                </label>
              )}
            />
          );

        case 'file':
        return (
          <Controller
            name={field.name as keyof FormData}
            control={control}
            render={({ field: { onChange, value } }) => (
              <FileUpload
                jiraKey={jiraKey}
                fieldName={field.name}
                acceptedTypes={field.validation?.fileTypes}
                maxSize={field.validation?.maxFileSize}
                onUploadComplete={onChange}
                currentValue={value}
                readonly={readonly}
              />
            )}
          />
        );

        case 'section':
          return null; // Section headers are rendered separately

      default:
        return <div>Unsupported field type: {field.type}</div>;
    }
  };

  return (
    <form className="space-y-6">
      {readonly && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <span className="text-blue-600">📋</span>
            <span className="text-sm font-medium text-blue-800">Form Submitted</span>
          </div>
          <p className="text-sm text-blue-700 mt-1">
            This form has been submitted and is now in review. Fields cannot be modified at this time.
          </p>
        </div>
      )}
      {(() => {
        // Group fields by section
        const sections: { [key: string]: FormField[] } = {};
        fields.forEach(field => {
          const section = getFieldSection(field.name);
          if (!sections[section]) {
            sections[section] = [];
          }
          sections[section].push(field);
        });

        // Render sections
        return Object.entries(sections).map(([sectionName, sectionFields]) => (
          <div key={sectionName} className={`screenshot-section-${sectionName} space-y-6`}>
            {sectionFields.map((field) => {
              // Handle section headers
              if (field.type === 'section') {
                return (
                  <div key={field.name} className="border-t border-gray-200 pt-6 mt-8">
                    <div className="flex items-center space-x-2 mb-4">
                      <h3 className="text-sm font-medium text-gray-700">{field.label}</h3>
                      {field.helpText && <HelpTooltip helpText={field.helpText} />}
                    </div>
                    {field.helperText && (
                      <p className="text-sm text-gray-600 mb-4">{field.helperText}</p>
                    )}
                  </div>
                );
              }
        
              // Special handling for App Icon field with inline checkbox
              if (field.name === 'appIconUrl') {
                const useLogoAsIconField = fields.find(f => f.name === 'useLogoAsIcon');
                const useLogoAsIconValue = watchedValues.useLogoAsIcon;
                
                return (
                  <div key={field.name} className={`space-y-2 screenshot-area-${field.name}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <label className="block text-sm font-medium text-gray-700">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {field.helpText && <HelpTooltip helpText={field.helpText} />}
                      </div>
                      {useLogoAsIconField && (
                        <Controller
                          name="useLogoAsIcon"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <label className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                className={`rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${readonly ? 'cursor-not-allowed opacity-50' : ''}`}
                                checked={value || false}
                                onChange={(e) => {
                                  if (readonly) return;
                                  onChange(e.target.checked);
                                }}
                                disabled={readonly}
                              />
                              <span className="text-sm text-gray-600">Same as logo</span>
                            </label>
                          )}
                        />
                      )}
                    </div>
                    
                    {!useLogoAsIconValue && (
                      <>
                        {renderField(field)}
                        {field.helperText && (
                          <p className="text-sm text-gray-500">{field.helperText}</p>
                        )}
                      </>
                    )}
                    
                    {useLogoAsIconValue && (
                      <p className="text-sm text-gray-500 italic">
                        Using company logo as app icon
                      </p>
                    )}
                    
                    {errors[field.name as keyof typeof errors] && (
                      <p className="text-sm text-red-600">
                        {errors[field.name as keyof typeof errors]?.message as string}
                      </p>
                    )}
                  </div>
                );
              }
              
              // Skip rendering the standalone checkbox field since it's now inline
              if (field.name === 'useLogoAsIcon') {
                return null;
              }
              
              // Conditional rendering for Custom Shop URL
              if (field.name === 'redirectWebsiteUrl') {
                const useWebsiteForShopValue = watchedValues.useWebsiteForShop;
                
                // Only render if the checkbox is checked
                if (!useWebsiteForShopValue) {
                  return null;
                }
                
                return (
                  <div key={field.name} className={`space-y-2 screenshot-area-${field.name}`}>
                    {field.label && (
                      <div className="flex items-center space-x-2">
                        <label className="block text-sm font-medium text-gray-700">
                          {field.label}
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        {field.helpText && <HelpTooltip helpText={field.helpText} />}
                      </div>
                    )}
                    
                    {renderField(field)}
                    {field.helperText && (
                      <p className="text-sm text-gray-500">{field.helperText}</p>
                    )}
                    
                    {errors[field.name as keyof typeof errors] && (
                      <p className="text-sm text-red-600">
                        {errors[field.name as keyof typeof errors]?.message as string}
                      </p>
                    )}
                  </div>
                );
              }
              
              // Default rendering for all other fields
              return (
                <div key={field.name} className={`space-y-2 screenshot-area-${field.name}`}>
                  {field.label && (
                    <div className="flex items-center space-x-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {field.helpText && <HelpTooltip helpText={field.helpText} />}
                    </div>
                  )}
                  
                  {renderField(field)}
                  
                  {field.helperText && (
                    <p className="text-sm text-gray-500">{field.helperText}</p>
                  )}
                  
                  {errors[field.name as keyof typeof errors] && (
                    <p className="text-sm text-red-600">
                      {errors[field.name as keyof typeof errors]?.message as string}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        ));
      })()}
    </form>
  );
}
